import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Container, Divider, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { api } from '../../../lib/api';
import { festivalPhase } from '../../../lib/festival';
import { SITE_URL } from '../../../lib/seo';

export default function BlogArticle() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, article: null, notFound: false });

  useEffect(() => {
    let active = true;
    setState({ loading: true, article: null, notFound: false });
    api
      .getArticle(slug)
      .then((data) => {
        if (active) setState({ loading: false, article: data.article, notFound: false });
      })
      .catch((err) => {
        if (active) setState({ loading: false, article: null, notFound: err.status === 404 });
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (state.loading) return null;

  if (!state.article) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: '2.2rem', mb: 2 }}>
          {t('blogHub.notFoundTitle')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 4 }}>{t('blogHub.notFoundBody')}</Typography>
        <Button component={RouterLink} to="/blog" variant="outlined" startIcon={<ArrowBackRoundedIcon />}>
          {t('blogHub.back')}
        </Button>
      </Container>
    );
  }

  const article = state.article;
  const phase = article.isFestival ? festivalPhase(article) : 'unknown';
  const canonical = `${SITE_URL}/blog/${article.slug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Organization', name: article.author || 'Al-Amen Technology' },
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    ...(article.coverImageUrl ? { image: [article.coverImageUrl] } : {}),
    mainEntityOfPage: canonical
  };

  return (
    <Box>
      <Helmet>
        <title>{article.seoTitle || `${article.title} | Al-Amen Technology`}</title>
        <meta name="description" content={article.seoDescription || article.summary} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.summary} />
        {article.coverImageUrl ? <meta property="og:image" content={article.coverImageUrl} /> : null}
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 9 } }}>
        <Button component={RouterLink} to="/blog" variant="text" startIcon={<ArrowBackRoundedIcon />} sx={{ color: 'text.secondary', mb: 3, pl: 0 }}>
          {t('blogHub.back')}
        </Button>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.75 }}>
          {article.category ? <Chip label={article.category} size="small" variant="outlined" /> : null}
          {article.isFestival ? <Chip label="Event" size="small" color="primary" /> : null}
        </Stack>

        <Typography component="h1" sx={{ fontFamily: 'var(--font-serif)', fontSize: { xs: '2.4rem', md: '3.4rem' }, lineHeight: 1.06, letterSpacing: '-0.02em', mb: 2 }}>
          {article.title}
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 4, color: 'text.secondary', fontSize: '0.9rem', flexWrap: 'wrap' }}>
          <Typography variant="body2">
            {t('blogHub.byAuthor')} {article.author}
          </Typography>
          <Typography variant="body2">
            {t('blogHub.publishedOn')}{' '}
            {new Date(article.publishedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Stack>

        {article.coverImageUrl ? (
          <Box component="img" src={article.coverImageUrl} alt={article.coverImageAlt || ''} loading="lazy" sx={{ width: '100%', borderRadius: 3, mb: 4 }} />
        ) : null}

        {article.isFestival && phase !== 'after' ? (
          <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, mb: 4, bgcolor: 'background.paper' }}>
            <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.secondary', mb: 1 }}>
              {article.eventName} · Stand {article.eventStand}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button component={RouterLink} to="/contact?type=festival" variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ bgcolor: 'text.primary', color: 'background.default', borderRadius: 999 }}>
                {t('home.festivalBanner.ctaPrimary')}
              </Button>
              <Button component="a" href="https://urbanai.al-amentech.io" target="_blank" rel="noopener noreferrer" variant="outlined" sx={{ borderRadius: 999 }}>
                {t('home.festivalBanner.ctaSecondary')}
              </Button>
            </Stack>
          </Paper>
        ) : null}

        <Box
          sx={{
            '& p': { color: 'text.primary', lineHeight: 1.75, mb: 2 },
            '& h2': { fontFamily: 'var(--font-serif)', fontSize: '1.8rem', mt: 4, mb: 1.5 },
            '& h3': { fontFamily: 'var(--font-serif)', fontSize: '1.4rem', mt: 3, mb: 1 },
            '& ul, & ol': { pl: 3, mb: 2 },
            '& li': { mb: 0.75, lineHeight: 1.65 },
            '& a': { color: 'primary.main' },
            '& strong': { fontWeight: 700 }
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.bodyMarkdown}</ReactMarkdown>
        </Box>

        <Divider sx={{ my: 5 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button component={RouterLink} to="/contact" variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ bgcolor: 'text.primary', color: 'background.default', borderRadius: 999 }}>
            {t('home.getInTouch.cta')}
          </Button>
          <Button component={RouterLink} to="/blog" variant="outlined" sx={{ borderRadius: 999 }}>
            {t('blogHub.relatedTitle')}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}
